// /Users/pramod/Desktop/school-erp/frontend/src/Head-Of-Institution/LiveMonitoring/components/FacultyDropdown.jsx

import React, { useState, useRef, useEffect } from 'react';
import { 
  ChevronDown, 
  User, 
  Search, 
  X, 
  Check
} from 'lucide-react';

const FacultyDropdown = ({ faculties, onSelect, placeholder = 'Search Teacher Schedule...' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selected, setSelected] = useState(null);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  const filteredFaculties = faculties.filter(f =>
    f.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    f.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getFacultyColor = (name) => {
    const colors = [
      'bg-indigo-100 text-indigo-700',
      'bg-blue-100 text-blue-700',
      'bg-emerald-100 text-emerald-700',
      'bg-purple-100 text-purple-700',
      'bg-pink-100 text-pink-700',
      'bg-orange-100 text-orange-700',
      'bg-teal-100 text-teal-700',
      'bg-rose-100 text-rose-700',
    ];
    if (!name) return colors[0];
    const index = name.length % colors.length;
    return colors[index];
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current.focus(), 150);
    }
  }, [isOpen]);

  const handleSelect = (faculty) => {
    setSelected(faculty);
    onSelect(faculty);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setSelected(null);
    onSelect(null);
    setSearchTerm('');
  };

  return (
    <div className="relative w-full sm:w-72" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`
          w-full px-4 py-2.5 bg-white border rounded-xl text-sm 
          flex items-center justify-between gap-2
          transition-all duration-200 min-h-[44px]
          ${isOpen ? 'border-indigo-400 ring-2 ring-indigo-100 shadow-xs' : 'border-gray-300 hover:border-gray-400'}
          ${selected ? 'bg-indigo-50/40' : 'bg-white'}
          focus:outline-none select-none
        `}
      >
        <span className="flex items-center gap-2.5 truncate min-w-0 text-left">
          {selected ? (
            <>
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${getFacultyColor(selected.name)}`}>
                {getInitials(selected.name)}
              </div>
              <span className="truncate font-semibold text-gray-800">{selected.name}</span>
            </>
          ) : (
            <>
              <div className="p-1 bg-gray-50 rounded-md text-gray-400">
                <User className="w-4 h-4" />
              </div>
              <span className="text-gray-500 truncate font-medium">{placeholder}</span>
            </>
          )}
        </span>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {selected && (
            <span
              role="button"
              tabIndex={0}
              onClick={handleClear}
              onKeyDown={(e) => e.key === 'Enter' && handleClear(e)}
              className="p-1 hover:bg-gray-200/80 rounded-full transition-colors text-gray-400 hover:text-gray-600 touch-none"
              title="Clear selection"
            >
              <X className="w-3.5 h-3.5" />
            </span>
          )}
          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {/* MOBILE MODAL BACKDROP (Hidden on desktop screens) */}
      {isOpen && (
        <div 
          className="sm:hidden fixed inset-0 z-[999998] bg-black/40 backdrop-blur-xs transition-opacity animate-fade-in"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* DROPDOWN VIEWPORT INTERFACE PANEL */}
      {isOpen && (
        <div className={`
          fixed bottom-0 left-0 right-0 z-[999999] rounded-t-2xl max-h-[75vh] 
          sm:absolute sm:bottom-auto sm:top-full sm:left-0 sm:right-0 sm:z-50 sm:rounded-xl sm:mt-1.5
          bg-white border border-gray-200 shadow-xl overflow-hidden flex flex-col
          animate-slide-up sm:animate-fade-in
        `}>
          {/* Top Notch Indicator Line for Mobile Drawer Drag Interface Context */}
          <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mt-2 mb-1 sm:hidden flex-shrink-0" />

          {/* Search Header Form Band */}
          <div className="p-3 border-b border-gray-100 bg-gray-50/60 flex-shrink-0">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Type instructor name..."
                className="w-full pl-9 pr-9 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white transition-all font-medium"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm('')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full text-gray-400"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <div className="flex items-center gap-1 mt-2 text-[10px] text-gray-400 px-1 font-medium">
              <span>Found {filteredFaculties.length} entries matching search</span>
            </div>
          </div>

          {/* Scrollable Faculty Selection List */}
          <div className="overflow-y-auto max-h-[45vh] sm:max-h-60 flex-1 divide-y divide-gray-50">
            {filteredFaculties.length === 0 ? (
              <div className="flex flex-col items-center justify-center p-8 text-center bg-white">
                <Search className="w-8 h-8 text-gray-200 mb-2" />
                <p className="text-sm font-semibold text-gray-600">No results match search</p>
                <p className="text-xs text-gray-400 mt-0.5">Double-check the entry name spelling details.</p>
              </div>
            ) : (
              filteredFaculties.map((faculty) => {
                const initials = getInitials(faculty.name);
                const colorClass = getFacultyColor(faculty.name);
                const isSelected = selected?.id === faculty.id;

                return (
                  <button
                    key={faculty.id}
                    type="button"
                    onClick={() => handleSelect(faculty)}
                    className={`
                      w-full px-4 py-3 text-left transition-colors
                      flex items-center gap-3 active:bg-gray-100/60
                      ${isSelected ? 'bg-indigo-50/50' : 'bg-white hover:bg-gray-50/80'}
                    `}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-bold flex-shrink-0 shadow-xs ${colorClass}`}>
                      {initials}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className={`text-sm font-bold truncate ${isSelected ? 'text-indigo-700' : 'text-gray-800'}`}>
                          {faculty.name}
                        </span>
                        {isSelected && (
                          <Check className="w-4 h-4 text-indigo-600 flex-shrink-0" />
                        )}
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>

          {/* Footer Metrics Label Strip */}
          <div className="p-2.5 border-t border-gray-100 bg-gray-50 text-[10px] text-gray-400 flex items-center justify-between flex-shrink-0">
            <span className="font-medium">{faculties.length} active registry records</span>
            <button 
              type="button"
              onClick={() => setIsOpen(false)}
              className="sm:hidden text-indigo-600 font-bold px-2 py-0.5 hover:bg-indigo-50 rounded"
            >
              Close Menu
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FacultyDropdown;